import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import mediaPlanningApi from '../api/mediaPlanning';

export const mediaPlanningQueryKeys = {
  plan: (brandId, month) => ['mediaPlanning', 'plan', String(brandId), month],
};

const STALE_MS = 5 * 60 * 1000; // los datos se sincronizan 2 veces por día

export function useMediaPlanningQuery(brandId, month, options = {}) {
  return useQuery({
    queryKey: mediaPlanningQueryKeys.plan(brandId, month),
    queryFn: () => mediaPlanningApi.getMediaPlanning(brandId, month),
    enabled: Boolean(brandId && month),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useCreateOverrideMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => mediaPlanningApi.createOverride(brandId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export function useDeleteOverrideMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (overrideId) => mediaPlanningApi.deleteOverride(brandId, overrideId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export function useApplyPreviewQuery(brandId, date, options = {}) {
  return useQuery({
    queryKey: ['mediaPlanning', 'apply-preview', String(brandId), date],
    queryFn: () => mediaPlanningApi.getApplyPreview(brandId, date),
    enabled: Boolean(brandId && date),
    staleTime: 0,
    ...options,
  });
}

export function useApplyPlanMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (date) => mediaPlanningApi.applyPlan(brandId, date, false),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export function useSetPlanMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => mediaPlanningApi.setPlan(brandId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export function useCreatePromoMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => mediaPlanningApi.createPromo(brandId, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export function useDeletePromoMutation(brandId, month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (promoId) => mediaPlanningApi.deletePromo(brandId, promoId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: mediaPlanningQueryKeys.plan(brandId, month) }),
    ...options,
  });
}

export const promoCalendarQueryKey = (month) => ['mediaPlanning', 'promo-calendar', month];

export function usePromoCalendarQuery(month, options = {}) {
  return useQuery({
    queryKey: promoCalendarQueryKey(month),
    queryFn: () => mediaPlanningApi.getPromoCalendar(month),
    enabled: Boolean(month),
    staleTime: STALE_MS,
    ...options,
  });
}

export function useCalendarCreatePromoMutation(month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, ...payload }) => mediaPlanningApi.createPromo(brandId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promoCalendarQueryKey(month) }),
    ...options,
  });
}

export function useCalendarDeletePromoMutation(month, options = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, promoId }) => mediaPlanningApi.deletePromo(brandId, promoId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promoCalendarQueryKey(month) }),
    ...options,
  });
}
